import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Play, Edit2, Trash2, CheckCircle, Plus, Loader2,
  Users, BarChart2, Save, X, RefreshCw, ChevronRight,
  ArrowLeft, ArrowUp, ArrowDown, Settings, Calendar, Hash, Star, MessageSquare,
  Gift, ExternalLink, ThumbsUp, Copy, RotateCcw, Key, Lock, Unlock,
  Upload, ImageIcon
} from 'lucide-react';

const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

// ─── Shared UI helpers ────────────────────────────────────────────────────────
const lbl = 'block text-cream-400 text-xs uppercase mb-1.5';
const inp = 'w-full bg-charcoal-100 border border-white/10 rounded-lg px-3 py-2 text-cream text-sm focus:border-gold/50 focus:outline-none';
const statusBadge = (s) => {
  const map = {
    active: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    completed: 'bg-gold/20 text-gold border-gold/30',
    cancelled: 'bg-red-500/20 text-red-400 border-red-500/30',
    paused: 'bg-amber-500/20 text-amber-400 border-amber-500/30'
  };
  return `text-xs border px-2 py-0.5 rounded-full ${map[s] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'}`;
};

// ─── Challenge List (top-level) ───────────────────────────────────────────────
const ChallengeList = ({ challenges, onSelect, onCreate, onDelete, loading }) => {
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ slug: '', title_en: '', title_ar: '', total_days: 21, description_en: '', description_ar: '' });
  const [saving, setSaving] = useState(false);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onCreate({ ...form, total_days: parseInt(form.total_days) || 1, is_active: true, allow_previous_days_access: true });
      setShowCreate(false);
      setForm({ slug: '', title_en: '', title_ar: '', total_days: 21, description_en: '', description_ar: '' });
    } catch (err) {
      toast.error(err.message || 'Failed to create challenge');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-gold" size={26} /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-cream text-base font-medium">{challenges.length} Challenge{challenges.length !== 1 ? 's' : ''}</h3>
        <button
          onClick={() => setShowCreate(s => !s)}
          className="flex items-center gap-2 px-4 py-2 bg-gold/10 text-gold border border-gold/30 text-sm rounded-lg hover:bg-gold/20 transition-all"
        >
          <Plus size={13} />
          New Challenge
        </button>
      </div>

      {/* Create form */}
      {showCreate && (
        <form onSubmit={handleCreate} className="border border-gold/20 bg-charcoal-100 rounded-xl p-5 space-y-4">
          <h4 className="text-cream text-sm font-medium mb-1">Create New Challenge</h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={lbl}>Title (EN) *</label>
              <input required className={inp} value={form.title_en} onChange={e => setForm(f => ({ ...f, title_en: e.target.value }))} placeholder="The Filter" />
            </div>
            <div>
              <label className={lbl}>Title (AR) *</label>
              <input required className={inp} dir="rtl" value={form.title_ar} onChange={e => setForm(f => ({ ...f, title_ar: e.target.value }))} placeholder="The Filter" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={lbl}>
                <Hash size={10} className="inline mr-1" />
                Slug * (url-safe)
              </label>
              <input required className={inp} value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))} placeholder="the-filter" />
            </div>
            <div>
              <label className={lbl}>
                <Calendar size={10} className="inline mr-1" />
                Total Days *
              </label>
              <input required type="number" min="1" max="365" className={inp} value={form.total_days} onChange={e => setForm(f => ({ ...f, total_days: e.target.value }))} />
            </div>
          </div>
          <div>
            <label className={lbl}>Description (EN)</label>
            <textarea className={`${inp} h-16 resize-none`} value={form.description_en} onChange={e => setForm(f => ({ ...f, description_en: e.target.value }))} />
          </div>
          <div>
            <label className={lbl}>Description (AR)</label>
            <textarea className={`${inp} h-16 resize-none`} dir="rtl" value={form.description_ar} onChange={e => setForm(f => ({ ...f, description_ar: e.target.value }))} />
          </div>
          <div className="flex gap-3">
            <button type="submit" disabled={saving} className="flex items-center gap-2 px-5 py-2.5 bg-gold text-charcoal text-sm font-medium rounded-lg hover:bg-gold/90 disabled:opacity-60 transition-all">
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              Create Challenge
            </button>
            <button type="button" onClick={() => setShowCreate(false)} className="px-5 py-2.5 border border-white/10 text-cream-400 text-sm rounded-lg hover:text-cream transition-colors">
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Challenge cards */}
      <div className="space-y-3">
        {challenges.map(c => (
          <div
            key={c.id}
            className="flex items-center gap-4 p-5 bg-charcoal-100 border border-white/5 hover:border-gold/20 rounded-xl group transition-all"
          >
            <button
              onClick={() => onSelect(c)}
              className="flex items-center gap-4 flex-1 min-w-0 text-left"
            >
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gold/10 border border-gold/20 flex flex-col items-center justify-center">
                <span className="text-gold font-bold text-lg leading-none">{c.total_days}</span>
                <span className="text-gold/60 text-[9px] uppercase">days</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-cream font-medium truncate">{c.title_en}</p>
                <p className="text-cream-400/60 text-xs truncate mt-0.5">{c.title_ar} · <code className="text-cream-400/40">{c.slug}</code></p>
              </div>
            </button>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className={`text-xs border px-2 py-0.5 rounded-full ${c.is_active ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 'bg-white/5 text-cream-400/40 border-white/10'}`}>
                {c.is_active ? 'Active' : 'Inactive'}
              </span>
              <button
                onClick={() => onDelete(c)}
                className="p-1.5 text-cream-400/40 hover:text-red-400 transition-colors"
                title="Delete challenge"
              >
                <Trash2 size={14} />
              </button>
              <ChevronRight size={15} className="text-cream-400/40 group-hover:text-gold transition-colors" />
            </div>
          </div>
        ))}

        {challenges.length === 0 && (
          <div className="text-center py-10 text-cream-400 text-sm">
            No challenges yet. Create your first one above.
          </div>
        )}
      </div>
    </div>
  );
};

// ─── StarDisplay ──────────────────────────────────────────────────────────────
const StarDisplay = ({ value }) => (
  <div className="flex items-center gap-0.5">
    {[1,2,3,4,5].map(n => (
      <Star key={n} size={12} className={n <= value ? 'text-gold fill-gold' : 'text-white/15'} />
    ))}
  </div>
);

// ─── Challenge Detail Manager ─────────────────────────────────────────────────
const ChallengeDetail = ({ challenge, getAuthHeader, onBack }) => {
  const [days, setDays] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [stats, setStats] = useState(null);
  const [feedback, setFeedback] = useState([]);
  const [upsellActions, setUpsellActions] = useState([]);
  const [view, setView] = useState('days');
  const [loading, setLoading] = useState(true);

  // Day editing
  const [editingDay, setEditingDay] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [thumbnailUploading, setThumbnailUploading] = useState(false);

  // Enrollment editing
  const [editingEnrollment, setEditingEnrollment] = useState(null);
  const [enrollForm, setEnrollForm] = useState({});

  // Duplicate
  const [showDuplicate, setShowDuplicate] = useState(false);
  const [duplicateSlug, setDuplicateSlug] = useState('');
  const [duplicating, setDuplicating] = useState(false);

  // Upsell editing
  const [editingUpsell, setEditingUpsell] = useState(null);
  const [upsellForm, setUpsellForm] = useState({});
  const [savingUpsell, setSavingUpsell] = useState(false);
  const [showUpsellCreate, setShowUpsellCreate] = useState(false);
  const [newUpsellForm, setNewUpsellForm] = useState({ label_en: '', label_ar: '', url: '', style: 'primary', sort_order: 0, is_active: true });

  // Settings
  const [settingsForm, setSettingsForm] = useState({
    is_active: challenge.is_active ?? true,
    allow_previous_days_access: challenge.allow_previous_days_access ?? true,
    unlock_mode: challenge.unlock_mode || 'daily',
    requires_code: challenge.requires_code ?? false,
    access_code: challenge.access_code || '',
    requires_payment: challenge.requires_payment ?? false,
    completion_required: challenge.completion_required ?? false,
    cohort_start_date: challenge.cohort_start_date || '',
    welcome_message_en: challenge.welcome_message_en || '',
    welcome_message_ar: challenge.welcome_message_ar || ''
  });
  const [savingSettings, setSavingSettings] = useState(false);

  // Generate days
  const [generating, setGenerating] = useState(false);
  const [generateDays, setGenerateDays] = useState(challenge.total_days);

  // Days sort direction
  const [sortAsc, setSortAsc] = useState(true);

  // Search
  const [search, setSearch] = useState('');

  const slug = challenge.slug;

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const [daysRes, enrRes, statsRes] = await Promise.all([
        fetch(`${backendUrl}/api/admin/challenges/${slug}/days`, { headers: getAuthHeader() }),
        fetch(`${backendUrl}/api/admin/challenges/${slug}/enrollments`, { headers: getAuthHeader() }),
        fetch(`${backendUrl}/api/admin/challenges/${slug}/stats`, { headers: getAuthHeader() })
      ]);
      if (daysRes.ok) setDays(await daysRes.json());
      if (enrRes.ok) setEnrollments(await enrRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (err) {
      console.error('Challenge detail fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [slug, getAuthHeader]);

  const fetchFeedback = useCallback(async () => {
    try {
      const res = await fetch(`${backendUrl}/api/admin/challenges/${slug}/feedback`, { headers: getAuthHeader() });
      if (res.ok) setFeedback(await res.json());
    } catch {}
  }, [slug, getAuthHeader]);

  const fetchUpsell = useCallback(async () => {
    try {
      const res = await fetch(`${backendUrl}/api/admin/challenges/${slug}/upsell`, { headers: getAuthHeader() });
      if (res.ok) setUpsellActions(await res.json());
    } catch {}
  }, [slug, getAuthHeader]);

  useEffect(() => {
    if (view === 'feedback') fetchFeedback();
    if (view === 'upsell') fetchUpsell();
  }, [view, fetchFeedback, fetchUpsell]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  // ── Duplicate challenge ──
  const handleDuplicate = async () => {
    if (!duplicateSlug) return;
    setDuplicating(true);
    try {
      const res = await fetch(`${backendUrl}/api/admin/challenges/${slug}/duplicate?new_slug=${encodeURIComponent(duplicateSlug)}`, {
        method: 'POST',
        headers: getAuthHeader()
      });
      if (res.ok) {
        toast.success(`Challenge duplicated as "${duplicateSlug}"`);
        setShowDuplicate(false);
        setDuplicateSlug('');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Duplication failed');
      }
    } catch { toast.error('Network error'); }
    finally { setDuplicating(false); }
  };

  // ── Approve/reject feedback ──
  const toggleFeedbackApproval = async (fb) => {
    try {
      const res = await fetch(`${backendUrl}/api/admin/challenges/${slug}/feedback/${fb.id}`, {
        method: 'PUT',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_approved: !fb.is_approved })
      });
      if (res.ok) {
        const updated = await res.json();
        setFeedback(prev => prev.map(f => f.id === updated.id ? updated : f));
        toast.success(!fb.is_approved ? 'Feedback approved' : 'Approval removed');
      }
    } catch { toast.error('Failed'); }
  };

  const toggleFeedbackFeatured = async (fb) => {
    try {
      const res = await fetch(`${backendUrl}/api/admin/challenges/${slug}/feedback/${fb.id}`, {
        method: 'PUT',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ featured: !fb.featured })
      });
      if (res.ok) {
        const updated = await res.json();
        setFeedback(prev => prev.map(f => f.id === updated.id ? updated : f));
      }
    } catch {}
  };

  // ── Upsell CRUD ──
  const createUpsell = async () => {
    setSavingUpsell(true);
    try {
      const res = await fetch(`${backendUrl}/api/admin/challenges/${slug}/upsell`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newUpsellForm, challenge_slug: slug })
      });
      if (res.ok) {
        const a = await res.json();
        setUpsellActions(prev => [...prev, a]);
        setShowUpsellCreate(false);
        setNewUpsellForm({ label_en: '', label_ar: '', url: '', style: 'primary', sort_order: 0, is_active: true });
        toast.success('Upsell action created');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Create failed');
      }
    } catch { toast.error('Network error'); }
    finally { setSavingUpsell(false); }
  };

  const saveUpsell = async () => {
    setSavingUpsell(true);
    try {
      const res = await fetch(`${backendUrl}/api/admin/challenges/${slug}/upsell/${editingUpsell.id}`, {
        method: 'PUT',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(upsellForm)
      });
      if (res.ok) {
        const updated = await res.json();
        setUpsellActions(prev => prev.map(a => a.id === updated.id ? updated : a));
        setEditingUpsell(null);
        toast.success('Upsell action saved');
      }
    } catch { toast.error('Network error'); }
    finally { setSavingUpsell(false); }
  };

  const deleteUpsell = async (actionId) => {
    if (!window.confirm('Delete this upsell action?')) return;
    try {
      await fetch(`${backendUrl}/api/admin/challenges/${slug}/upsell/${actionId}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      setUpsellActions(prev => prev.filter(a => a.id !== actionId));
      toast.success('Deleted');
    } catch { toast.error('Delete failed'); }
  };

  // ── Reset enrollment ──
  const resetEnrollment = async (enrollmentId) => {
    if (!window.confirm('Reset this enrollment back to Day 1? All progress will be cleared.')) return;
    try {
      const res = await fetch(`${backendUrl}/api/admin/challenges/${slug}/enrollments/${enrollmentId}/reset`, {
        method: 'POST',
        headers: getAuthHeader()
      });
      if (res.ok) {
        toast.success('Enrollment reset to Day 1');
        setEditingEnrollment(null);
        fetchDetail();
      } else {
        toast.error('Reset failed');
      }
    } catch { toast.error('Network error'); }
  };

  // ── Generate days ──
  const handleGenerateDays = async () => {
    const n = parseInt(generateDays);
    if (!n || n < 1) { toast.error('Enter a valid number of days'); return; }
    setGenerating(true);
    try {
      const res = await fetch(`${backendUrl}/api/admin/challenges/${slug}/generate-days`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ total_days: n, overwrite: false })
      });
      if (res.ok) {
        const d = await res.json();
        toast.success(`Created ${d.total_created} day${d.total_created !== 1 ? 's' : ''}, skipped ${d.skipped.length} existing`);
        setTotalDays(n);
        const daysRes = await fetch(`${backendUrl}/api/admin/challenges/${slug}/days`, { headers: getAuthHeader() });
        if (daysRes.ok) setDays(await daysRes.json());
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Generation failed');
      }
    } catch {
      toast.error('Network error');
    } finally {
      setGenerating(false);
    }
  };

  // ── Upload thumbnail ──
  const uploadThumbnail = async (file) => {
    if (!file) return;
    setThumbnailUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', 'thumbnails');
      const res = await fetch(`${backendUrl}/api/admin/upload`, {
        method: 'POST',
        headers: getAuthHeader(),
        body: formData
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      const uploadedUrl = data.url || '';
      setEditForm(f => ({ ...f, thumbnail_url: uploadedUrl }));
      toast.success('Thumbnail uploaded');
    } catch (err) {
      toast.error(err.message || 'Failed to upload thumbnail');
    } finally {
      setThumbnailUploading(false);
    }
  };

  // ── Save day edit ──
  const saveDay = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${backendUrl}/api/admin/challenges/${slug}/days/${editingDay.id}`, {
        method: 'PUT',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm)
      });
      if (res.ok) {
        const updated = await res.json();
        setDays(prev => prev.map(d => d.id === updated.id ? updated : d));
        setEditingDay(null);
        toast.success('Day saved');
      } else {
        toast.error('Save failed');
      }
    } catch {
      toast.error('Network error');
    } finally {
      setSaving(false);
    }
  };

  // ── Toggle published ──
  const togglePublished = async (day) => {
    try {
      const res = await fetch(`${backendUrl}/api/admin/challenges/${slug}/days/${day.id}`, {
        method: 'PUT',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_published: !day.is_published })
      });
      if (res.ok) {
        const updated = await res.json();
        setDays(prev => prev.map(d => d.id === updated.id ? updated : d));
      }
    } catch {
      toast.error('Failed to toggle');
    }
  };

  // ── Save enrollment override ──
  const saveEnrollment = async () => {
    try {
      const res = await fetch(`${backendUrl}/api/admin/challenges/${slug}/enrollments/${editingEnrollment.id}`, {
        method: 'PUT',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          current_day_override: enrollForm.current_day_override ? parseInt(enrollForm.current_day_override) : null,
          is_completed: enrollForm.is_completed,
          status: enrollForm.status,
          admin_notes: enrollForm.admin_notes
        })
      });
      if (res.ok) {
        const updated = await res.json();
        setEnrollments(prev => prev.map(e => e.id === updated.id ? { ...e, ...updated } : e));
        setEditingEnrollment(null);
        toast.success('Enrollment updated');
      }
    } catch {
      toast.error('Failed to update');
    }
  };

  // ── Save settings ──
  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const res = await fetch(`${backendUrl}/api/admin/challenges/${challenge.id}`, {
        method: 'PUT',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(settingsForm)
      });
      if (res.ok) {
        toast.success('Settings saved');
      } else {
        toast.error('Failed to save');
      }
    } catch {
      toast.error('Network error');
    } finally {
      setSavingSettings(false);
    }
  };

  const filteredEnrollments = enrollments.filter(e =>
    !search ||
    e.user_name?.toLowerCase().includes(search.toLowerCase()) ||
    e.user_email?.toLowerCase().includes(search.toLowerCase()) ||
    e.phone?.toLowerCase().includes(search.toLowerCase())
  );

  const exportEnrollmentsCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Enrolled Date', 'Current Day', 'Status', 'Notes'];
    const rows = filteredEnrollments.map(e => [
      e.user_name || '',
      e.user_email || '',
      e.phone || '',
      e.enrollment_date?.slice(0, 10) || '',
      e.current_day_override ? `${e.current_day_override} (override)` : (e.computed_day || ''),
      e.status || '',
      e.admin_notes || ''
    ]);
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const safeName = (challenge.title_en || challenge.slug || 'challenge').replace(/\s+/g, '_').toLowerCase();
    link.href = url;
    link.download = `${safeName}_enrollments_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filteredEnrollments.length} enrollments`);
  };

  const [totalDays, setTotalDays] = useState(challenge.total_days);
  const publishedCount = days.filter(d => d.is_published).length;

  return (
    <div className="space-y-6">
      {/* Back button + header + duplicate */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-2 text-cream-400 hover:text-cream border border-white/10 rounded-lg transition-colors">
          <ArrowLeft size={15} />
        </button>
        <div className="flex-1 min-w-0">
          <h3 className="text-cream font-medium">{challenge.title_en}</h3>
          <p className="text-cream-400/60 text-xs">{challenge.total_days}-day challenge · <code className="text-cream-400/40">{slug}</code></p>
        </div>
        <button
          onClick={() => setShowDuplicate(s => !s)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-white/10 text-cream-400 hover:text-gold hover:border-gold/30 rounded-lg transition-all"
        >
          <Copy size={12} />
          Duplicate
        </button>
      </div>

      {/* Duplicate dialog */}
      {showDuplicate && (
        <div className="border border-gold/20 bg-charcoal-100 rounded-xl p-4 space-y-3">
          <p className="text-cream text-sm font-medium">Duplicate Challenge</p>
          <p className="text-cream-400 text-xs">All days will be copied (unpublished). The new challenge will be inactive.</p>
          <div className="flex gap-2">
            <input
              className={inp}
              placeholder="new-slug (e.g. the-filter-v2)"
              value={duplicateSlug}
              onChange={e => setDuplicateSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            />
            <button
              onClick={handleDuplicate}
              disabled={duplicating || !duplicateSlug}
              className="px-4 py-2 bg-gold text-charcoal text-xs font-medium rounded-lg hover:bg-gold/90 transition-all disabled:opacity-60 flex items-center gap-1.5 flex-shrink-0"
            >
              {duplicating ? <Loader2 size={12} className="animate-spin" /> : <Copy size={12} />}
              Duplicate
            </button>
          </div>
        </div>
      )}

      {/* Stats strip */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Enrolled', value: stats.total_enrolled, color: 'text-gold' },
            { label: 'Active', value: stats.active, color: 'text-emerald-400' },
            { label: 'Completed', value: stats.completed, color: 'text-blue-400' },
            { label: 'Days Ready', value: `${publishedCount}/${totalDays}`, color: 'text-cream' }
          ].map(s => (
            <div key={s.label} className="bg-charcoal-100 border border-white/5 rounded-xl p-4">
              <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-cream-400 text-xs mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Tab strip */}
      <div className="flex gap-1 border-b border-white/5 pb-0 flex-wrap">
        {[
          { id: 'days', label: `Days (${days.length}/${totalDays})` },
          { id: 'enrollments', label: `Enrollments (${enrollments.length})` },
          { id: 'feedback', label: `Feedback` },
          { id: 'upsell', label: `Upsell` },
          { id: 'settings', label: 'Settings' }
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setView(t.id)}
            className={`px-4 py-2 text-sm transition-colors border-b-2 -mb-px ${
              view === t.id ? 'border-gold text-gold' : 'border-transparent text-cream-400 hover:text-cream'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading && <div className="flex items-center justify-center py-10"><Loader2 className="animate-spin text-gold" size={24} /></div>}

      {/* ── Days view ── */}
      {!loading && view === 'days' && (() => {
        const sortedDays = [...days].sort((a, b) =>
          sortAsc
            ? parseInt(a.day_number) - parseInt(b.day_number)
            : parseInt(b.day_number) - parseInt(a.day_number)
        );
        return (
        <div className="space-y-4">
          {/* Generate days toolbar */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="1"
                max="365"
                value={generateDays}
                onChange={e => setGenerateDays(e.target.value)}
                className="w-20 bg-charcoal-100 border border-white/10 rounded-lg px-3 py-2 text-cream text-sm focus:border-gold/50 focus:outline-none"
                title="Number of days to generate"
              />
              <button
                onClick={handleGenerateDays}
                disabled={generating}
                className="px-4 py-2 bg-gold/10 text-gold border border-gold/30 text-sm rounded-lg hover:bg-gold/20 transition-all flex items-center gap-2 disabled:opacity-60"
              >
                {generating ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
                Generate Days
              </button>
            </div>
            <button onClick={fetchDetail} className="p-2 text-cream-400 hover:text-cream border border-white/10 rounded-lg transition-colors">
              <RefreshCw size={14} />
            </button>
            <button
              onClick={() => setSortAsc(s => !s)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-white/10 text-cream-400 hover:text-gold hover:border-gold/30 rounded-lg transition-all"
              title={sortAsc ? 'Switch to descending order' : 'Switch to ascending order'}
            >
              {sortAsc ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
              {sortAsc ? '0 → 1' : '1 → 0'}
            </button>
            <span className="text-cream-400/50 text-xs">
              {days.length < totalDays
                ? `${totalDays - days.length} day${totalDays - days.length !== 1 ? 's' : ''} missing`
                : 'All days created'}
            </span>
          </div>

          <div className="space-y-2">
            {sortedDays.map(day => (
              <div key={day.id} className="bg-charcoal-100 border border-white/5 rounded-xl p-4 flex items-center gap-4">
                <div className="w-9 h-9 rounded-lg bg-gold/10 border border-gold/20 flex items-center justify-center flex-shrink-0 text-gold font-bold text-sm">
                  {day.day_number}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-cream text-sm font-medium truncate">{day.title_en}</p>
                  <p className="text-cream-400/60 text-xs truncate">{day.title_ar}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Play size={12} className={day.video_url ? 'text-emerald-400' : 'text-white/20'} />
                  <button
                    onClick={() => togglePublished(day)}
                    className={`text-xs border px-2 py-0.5 rounded-full transition-colors ${
                      day.is_published
                        ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
                        : 'bg-white/5 text-cream-400/50 border-white/10'
                    }`}
                  >
                    {day.is_published ? 'Live' : 'Draft'}
                  </button>
                  <button
                    onClick={() => { setEditingDay(day); setEditForm({ ...day }); }}
                    className="p-1.5 text-cream-400 hover:text-gold transition-colors"
                  >
                    <Edit2 size={13} />
                  </button>
                  <button
                    onClick={async () => {
                      if (!window.confirm('Are you sure you want to delete this day? This action cannot be undone.')) return;
                      try {
                        const res = await fetch(`${backendUrl}/api/admin/challenges/${slug}/days/${day.id}`, {
                          method: 'DELETE',
                          headers: getAuthHeader()
                        });
                        if (res.ok) {
                          setDays(prev => prev.filter(d => d.id !== day.id));
                          toast.success(`Day ${day.day_number} deleted`);
                        } else {
                          toast.error('Failed to delete day');
                        }
                      } catch {
                        toast.error('Network error');
                      }
                    }}
                    className="p-1.5 text-cream-400/40 hover:text-red-400 transition-colors"
                    title="Delete day"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
            {days.length === 0 && (
              <div className="text-center py-10 text-cream-400 text-sm">
                No days yet. Use "Generate Days" above to create all {totalDays} day records.
              </div>
            )}
          </div>
        </div>
        );
      })()}

      {/* ── Enrollments view ── */}
      {!loading && view === 'enrollments' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <input
              type="text"
              placeholder="Search by name, email or phone..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className={`${inp} flex-1`}
            />
            <button
              onClick={exportEnrollmentsCSV}
              disabled={filteredEnrollments.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-gold/10 border border-gold/30 text-gold text-xs font-medium rounded-lg hover:bg-gold/20 transition-all disabled:opacity-40 whitespace-nowrap"
            >
              <ExternalLink size={13} />
              Export CSV ({filteredEnrollments.length})
            </button>
          </div>
          <div className="overflow-x-auto rounded-xl border border-white/5">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/5">
                  {['Name', 'Email', 'Phone', 'Enrolled', `Day (of ${totalDays})`, 'Status', ''].map(h => (
                    <th key={h} className="text-left text-cream-400 text-xs uppercase px-4 py-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredEnrollments.map(e => (
                  <tr key={e.id} className="border-b border-white/5 hover:bg-white/2 transition-colors">
                    <td className="px-4 py-3 text-cream">{e.user_name}</td>
                    <td className="px-4 py-3 text-cream-300">{e.user_email}</td>
                    <td className="px-4 py-3 text-cream-400 text-xs font-mono">{e.phone || <span className="text-white/20">—</span>}</td>
                    <td className="px-4 py-3 text-cream-400 text-xs">{e.enrollment_date?.slice(0, 10)}</td>
                    <td className="px-4 py-3">
                      <span className="text-gold font-medium">
                        {e.current_day_override
                          ? `${e.current_day_override} ✦`
                          : (e.computed_day || '–')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={statusBadge(e.status)}>{e.status}</span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => {
                          setEditingEnrollment(e);
                          setEnrollForm({ current_day_override: e.current_day_override || '', is_completed: e.is_completed, status: e.status, admin_notes: e.admin_notes || '' });
                        }}
                        className="p-1.5 text-cream-400 hover:text-gold transition-colors"
                      >
                        <Edit2 size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredEnrollments.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-cream-400">No enrollments yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Feedback view ── */}
      {!loading && view === 'feedback' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-cream-400 text-sm">{feedback.length} submission{feedback.length !== 1 ? 's' : ''}</p>
            <button onClick={fetchFeedback} className="p-2 text-cream-400 hover:text-cream border border-white/10 rounded-lg transition-colors">
              <RefreshCw size={14} />
            </button>
          </div>
          {feedback.length === 0 && (
            <div className="text-center py-12 text-cream-400/50 text-sm">No feedback yet.</div>
          )}
          {feedback.map(fb => (
            <div key={fb.id} className="bg-charcoal-100 border border-white/5 rounded-xl p-5 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-cream text-sm font-medium">{fb.user_name || 'Anonymous'}</p>
                  <p className="text-cream-400/60 text-xs">{fb.user_email} · {fb.created_at?.slice(0,10)}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {fb.rating > 0 && <StarDisplay value={fb.rating} />}
                  {fb.nps_score !== null && fb.nps_score !== undefined && (
                    <span className="text-xs border border-white/10 text-cream-400 px-2 py-0.5 rounded-full">NPS {fb.nps_score}</span>
                  )}
                </div>
              </div>
              {fb.testimonial && (
                <p className="text-cream-300 text-sm italic leading-relaxed border-l-2 border-gold/30 pl-3">"{fb.testimonial}"</p>
              )}
              {fb.reflection && (
                <p className="text-cream-400/60 text-xs leading-relaxed">
                  <span className="text-cream-400/40 uppercase tracking-wider text-[10px]">Reflection: </span>
                  {fb.reflection}
                </p>
              )}
              <div className="flex items-center gap-3 pt-1">
                <button
                  onClick={() => toggleFeedbackApproval(fb)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-all ${
                    fb.is_approved
                      ? 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400'
                      : 'border-white/10 text-cream-400 hover:border-emerald-500/30 hover:text-emerald-400'
                  }`}
                >
                  <ThumbsUp size={11} />
                  {fb.is_approved ? 'Approved' : 'Approve'}
                </button>
                <button
                  onClick={() => toggleFeedbackFeatured(fb)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-all ${
                    fb.featured
                      ? 'bg-gold/20 border-gold/30 text-gold'
                      : 'border-white/10 text-cream-400 hover:border-gold/30 hover:text-gold'
                  }`}
                >
                  <Star size={11} />
                  {fb.featured ? 'Featured' : 'Feature'}
                </button>
                {fb.permission_to_share && (
                  <span className="text-xs text-cream-400/40 ml-auto">✓ permission to share</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Upsell view ── */}
      {!loading && view === 'upsell' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-cream-400 text-sm">{upsellActions.length} action{upsellActions.length !== 1 ? 's' : ''}</p>
            <button
              onClick={() => setShowUpsellCreate(s => !s)}
              className="flex items-center gap-2 px-4 py-2 bg-gold/10 text-gold border border-gold/30 text-sm rounded-lg hover:bg-gold/20 transition-all"
            >
              <Plus size={13} />
              Add CTA
            </button>
          </div>

          {/* Create form */}
          {showUpsellCreate && (
            <div className="border border-gold/20 bg-charcoal-100 rounded-xl p-5 space-y-4">
              <h4 className="text-cream text-sm font-medium">New Upsell CTA</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={lbl}>Label (EN)</label>
                  <input className={inp} value={newUpsellForm.label_en} onChange={e => setNewUpsellForm(f => ({ ...f, label_en: e.target.value }))} placeholder="Join the next program" />
                </div>
                <div>
                  <label className={lbl}>Label (AR)</label>
                  <input className={inp} dir="rtl" value={newUpsellForm.label_ar} onChange={e => setNewUpsellForm(f => ({ ...f, label_ar: e.target.value }))} placeholder="انضمي للبرنامج التالي" />
                </div>
              </div>
              <div>
                <label className={lbl}>URL</label>
                <input className={inp} value={newUpsellForm.url} onChange={e => setNewUpsellForm(f => ({ ...f, url: e.target.value }))} placeholder="https://... or /#section" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className={lbl}>Style</label>
                  <select className={inp} value={newUpsellForm.style} onChange={e => setNewUpsellForm(f => ({ ...f, style: e.target.value }))}>
                    <option value="primary">Primary (gold fill)</option>
                    <option value="outline">Outline</option>
                  </select>
                </div>
                <div>
                  <label className={lbl}>Sort Order</label>
                  <input type="number" className={inp} value={newUpsellForm.sort_order} onChange={e => setNewUpsellForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))} />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer pb-2">
                    <input type="checkbox" checked={newUpsellForm.is_active} onChange={e => setNewUpsellForm(f => ({ ...f, is_active: e.target.checked }))} className="accent-gold w-4 h-4" />
                    <span className="text-cream-300 text-sm">Active</span>
                  </label>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={createUpsell} disabled={savingUpsell} className="flex items-center gap-2 px-5 py-2.5 bg-gold text-charcoal text-sm font-medium rounded-lg hover:bg-gold/90 disabled:opacity-60 transition-all">
                  {savingUpsell ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                  Create
                </button>
                <button onClick={() => setShowUpsellCreate(false)} className="px-5 py-2.5 border border-white/10 text-cream-400 text-sm rounded-lg hover:text-cream transition-colors">Cancel</button>
              </div>
            </div>
          )}

          {/* Upsell list */}
          <div className="space-y-2">
            {upsellActions.map(a => (
              editingUpsell?.id === a.id ? (
                <div key={a.id} className="border border-gold/20 bg-charcoal-100 rounded-xl p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={lbl}>Label (EN)</label>
                      <input className={inp} value={upsellForm.label_en || ''} onChange={e => setUpsellForm(f => ({ ...f, label_en: e.target.value }))} />
                    </div>
                    <div>
                      <label className={lbl}>Label (AR)</label>
                      <input className={inp} dir="rtl" value={upsellForm.label_ar || ''} onChange={e => setUpsellForm(f => ({ ...f, label_ar: e.target.value }))} />
                    </div>
                  </div>
                  <div>
                    <label className={lbl}>URL</label>
                    <input className={inp} value={upsellForm.url || ''} onChange={e => setUpsellForm(f => ({ ...f, url: e.target.value }))} />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className={lbl}>Style</label>
                      <select className={inp} value={upsellForm.style || 'primary'} onChange={e => setUpsellForm(f => ({ ...f, style: e.target.value }))}>
                        <option value="primary">Primary</option>
                        <option value="outline">Outline</option>
                      </select>
                    </div>
                    <div>
                      <label className={lbl}>Sort Order</label>
                      <input type="number" className={inp} value={upsellForm.sort_order ?? 0} onChange={e => setUpsellForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))} />
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center gap-2 cursor-pointer pb-2">
                        <input type="checkbox" checked={!!upsellForm.is_active} onChange={e => setUpsellForm(f => ({ ...f, is_active: e.target.checked }))} className="accent-gold w-4 h-4" />
                        <span className="text-cream-300 text-sm">Active</span>
                      </label>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={saveUpsell} disabled={savingUpsell} className="flex items-center gap-2 px-4 py-2 bg-gold text-charcoal text-sm font-medium rounded-lg hover:bg-gold/90 disabled:opacity-60 transition-all">
                      {savingUpsell ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                      Save
                    </button>
                    <button onClick={() => setEditingUpsell(null)} className="px-4 py-2 border border-white/10 text-cream-400 text-sm rounded-lg hover:text-cream transition-colors">Cancel</button>
                  </div>
                </div>
              ) : (
                <div key={a.id} className="flex items-center gap-3 p-4 bg-charcoal-100 border border-white/5 rounded-xl">
                  <div className="flex-1 min-w-0">
                    <p className="text-cream text-sm">{a.label_en}</p>
                    <p className="text-cream-400/60 text-xs truncate">{a.url}</p>
                  </div>
                  <span className={`text-xs border px-2 py-0.5 rounded-full ${
                    a.style === 'primary' ? 'bg-gold/20 text-gold border-gold/30' : 'border-white/10 text-cream-400'
                  }`}>{a.style}</span>
                  <span className={`text-xs border px-2 py-0.5 rounded-full ${
                    a.is_active ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 'bg-white/5 text-cream-400/40 border-white/10'
                  }`}>{a.is_active ? 'Active' : 'Off'}</span>
                  <button onClick={() => { setEditingUpsell(a); setUpsellForm({ ...a }); }} className="p-1.5 text-cream-400 hover:text-gold transition-colors"><Edit2 size={13} /></button>
                  <button onClick={() => deleteUpsell(a.id)} className="p-1.5 text-cream-400 hover:text-red-400 transition-colors"><Trash2 size={13} /></button>
                </div>
              )
            ))}
            {upsellActions.length === 0 && !showUpsellCreate && (
              <div className="text-center py-10 text-cream-400/50 text-sm">No upsell CTAs yet. Add one above.</div>
            )}
          </div>
        </div>
      )}

      {/* ── Settings view ── */}
      {!loading && view === 'settings' && (
        <div className="max-w-lg space-y-6">
          <div className="bg-charcoal-100 border border-white/5 rounded-xl p-6 space-y-5">
            <h3 className="text-cream text-sm font-medium">Challenge Settings</h3>

            <div className="flex items-center justify-between text-sm">
              <span className="text-cream-400">Total days: <span className="text-gold font-medium">{challenge.total_days}</span></span>
              <span className="text-cream-400/50 text-xs">(edit from challenge list)</span>
            </div>

            {/* Active toggle */}
            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-cream-300 text-sm">Challenge is Active</span>
              <button
                onClick={() => setSettingsForm(f => ({ ...f, is_active: !f.is_active }))}
                className={`w-11 h-6 rounded-full transition-colors relative ${settingsForm.is_active ? 'bg-gold' : 'bg-white/10'}`}
              >
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${settingsForm.is_active ? 'left-6' : 'left-1'}`} />
              </button>
            </label>

            {/* Previous days */}
            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-cream-300 text-sm">Allow access to previous days</span>
              <button
                onClick={() => setSettingsForm(f => ({ ...f, allow_previous_days_access: !f.allow_previous_days_access }))}
                className={`w-11 h-6 rounded-full transition-colors relative ${settingsForm.allow_previous_days_access ? 'bg-gold' : 'bg-white/10'}`}
              >
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${settingsForm.allow_previous_days_access ? 'left-6' : 'left-1'}`} />
              </button>
            </label>

            {/* Completion required */}
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <span className="text-cream-300 text-sm">Completion required to unlock next day</span>
                <p className="text-cream-400/50 text-xs mt-0.5">User must mark a day complete before next day unlocks</p>
              </div>
              <button
                onClick={() => setSettingsForm(f => ({ ...f, completion_required: !f.completion_required }))}
                className={`w-11 h-6 rounded-full transition-colors relative flex-shrink-0 ml-4 ${settingsForm.completion_required ? 'bg-gold' : 'bg-white/10'}`}
              >
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${settingsForm.completion_required ? 'left-6' : 'left-1'}`} />
              </button>
            </label>

            {/* Unlock mode */}
            <div>
              <label className={lbl}>
                <Unlock size={10} className="inline mr-1" />
                Unlock Mode
              </label>
              <select
                className={inp}
                value={settingsForm.unlock_mode}
                onChange={e => setSettingsForm(f => ({ ...f, unlock_mode: e.target.value }))}
              >
                <option value="daily">Daily (one new day per calendar day)</option>
                <option value="immediate">Immediate (all days open at once)</option>
                <option value="cohort">Cohort (based on shared cohort start date)</option>
              </select>
            </div>

            {/* Cohort start date — shown only when mode = cohort */}
            {settingsForm.unlock_mode === 'cohort' && (
              <div>
                <label className={lbl}><Calendar size={10} className="inline mr-1" />Cohort Start Date</label>
                <input
                  type="date"
                  className={inp}
                  value={settingsForm.cohort_start_date}
                  onChange={e => setSettingsForm(f => ({ ...f, cohort_start_date: e.target.value }))}
                />
              </div>
            )}

            {/* Requires code */}
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <span className="text-cream-300 text-sm">Requires access code to enroll</span>
              </div>
              <button
                onClick={() => setSettingsForm(f => ({ ...f, requires_code: !f.requires_code }))}
                className={`w-11 h-6 rounded-full transition-colors relative flex-shrink-0 ml-4 ${settingsForm.requires_code ? 'bg-gold' : 'bg-white/10'}`}
              >
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${settingsForm.requires_code ? 'left-6' : 'left-1'}`} />
              </button>
            </label>

            {settingsForm.requires_code && (
              <div>
                <label className={lbl}><Key size={10} className="inline mr-1" />Access Code</label>
                <input className={inp} value={settingsForm.access_code} onChange={e => setSettingsForm(f => ({ ...f, access_code: e.target.value }))} placeholder="Enter the required code" />
              </div>
            )}

            {/* Requires payment */}
            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-cream-300 text-sm">Requires payment</span>
              <button
                onClick={() => setSettingsForm(f => ({ ...f, requires_payment: !f.requires_payment }))}
                className={`w-11 h-6 rounded-full transition-colors relative flex-shrink-0 ml-4 ${settingsForm.requires_payment ? 'bg-gold' : 'bg-white/10'}`}
              >
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${settingsForm.requires_payment ? 'left-6' : 'left-1'}`} />
              </button>
            </label>

            {/* Welcome messages */}
            <div>
              <label className={lbl}>Welcome Message (EN)</label>
              <textarea
                className={`${inp} h-16 resize-none`}
                value={settingsForm.welcome_message_en}
                onChange={e => setSettingsForm(f => ({ ...f, welcome_message_en: e.target.value }))}
                placeholder="Shown to users on first enrollment..."
              />
            </div>
            <div>
              <label className={lbl}>Welcome Message (AR)</label>
              <textarea
                className={`${inp} h-16 resize-none`}
                dir="rtl"
                value={settingsForm.welcome_message_ar}
                onChange={e => setSettingsForm(f => ({ ...f, welcome_message_ar: e.target.value }))}
                placeholder="رسالة الترحيب باللغة العربية..."
              />
            </div>

            <button
              onClick={saveSettings}
              disabled={savingSettings}
              className="flex items-center gap-2 px-5 py-2.5 bg-gold text-charcoal text-sm font-medium rounded-lg hover:bg-gold/90 transition-all disabled:opacity-60"
            >
              {savingSettings ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              Save Settings
            </button>
          </div>

          <div className="bg-charcoal-100 border border-white/5 rounded-xl p-5 space-y-2">
            <h3 className="text-cream text-sm font-medium">Public URL</h3>
            <code className="text-gold text-xs block">/challenge/{slug}</code>
          </div>
        </div>
      )}

      {/* ── Day edit modal ── */}
      {editingDay && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-charcoal border border-white/10 rounded-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
              <h3 className="text-cream font-medium">Edit Day {editingDay.day_number}</h3>
              <button onClick={() => setEditingDay(null)} className="text-cream-400 hover:text-cream"><X size={18} /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={lbl}>Title (EN)</label>
                  <input className={inp} value={editForm.title_en || ''} onChange={e => setEditForm(f => ({ ...f, title_en: e.target.value }))} />
                </div>
                <div>
                  <label className={lbl}>Title (AR)</label>
                  <input className={inp} dir="rtl" value={editForm.title_ar || ''} onChange={e => setEditForm(f => ({ ...f, title_ar: e.target.value }))} />
                </div>
              </div>
              <div>
                <label className={lbl}>Description (EN)</label>
                <textarea className={`${inp} h-20 resize-none`} value={editForm.short_description_en || ''} onChange={e => setEditForm(f => ({ ...f, short_description_en: e.target.value }))} />
              </div>
              <div>
                <label className={lbl}>Description (AR)</label>
                <textarea className={`${inp} h-20 resize-none`} dir="rtl" value={editForm.short_description_ar || ''} onChange={e => setEditForm(f => ({ ...f, short_description_ar: e.target.value }))} />
              </div>
              <div>
                <label className={lbl}>Video URL (YouTube / Vimeo / direct)</label>
                <input className={inp} placeholder="https://..." value={editForm.video_url || ''} onChange={e => setEditForm(f => ({ ...f, video_url: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={lbl}>Thumbnail</label>
                  {/* Preview */}
                  {editForm.thumbnail_url && (
                    <div className="mb-2 relative group">
                      <img
                        src={editForm.thumbnail_url.startsWith('/') ? `${backendUrl}${editForm.thumbnail_url}` : editForm.thumbnail_url}
                        alt="Thumbnail preview"
                        className="w-full h-24 object-cover rounded-lg border border-white/10"
                        onError={e => { e.target.style.display = 'none'; }}
                      />
                      <button
                        type="button"
                        onClick={() => setEditForm(f => ({ ...f, thumbnail_url: '' }))}
                        className="absolute top-1 right-1 bg-black/60 hover:bg-red-500/80 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remove thumbnail"
                      >
                        <X size={10} />
                      </button>
                    </div>
                  )}
                  {/* Upload zone */}
                  <label className={`flex flex-col items-center justify-center gap-1.5 w-full border border-dashed border-white/20 rounded-lg py-3 px-2 cursor-pointer hover:border-gold/50 hover:bg-gold/5 transition-all ${thumbnailUploading ? 'opacity-60 pointer-events-none' : ''}`}>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={thumbnailUploading}
                      onChange={e => { const f = e.target.files?.[0]; if (f) uploadThumbnail(f); e.target.value = ''; }}
                    />
                    {thumbnailUploading
                      ? <Loader2 size={14} className="animate-spin text-gold" />
                      : <Upload size={14} className="text-cream-400" />
                    }
                    <span className="text-cream-400 text-[11px] text-center">
                      {thumbnailUploading ? 'Uploading…' : (editForm.thumbnail_url ? 'Replace image' : 'Upload image')}
                    </span>
                  </label>
                  {/* Fallback URL input — shown when no image uploaded yet */}
                  {!editForm.thumbnail_url && (
                    <input
                      className={`${inp} mt-1.5 text-xs`}
                      placeholder="or paste external URL…"
                      value={editForm.thumbnail_url || ''}
                      onChange={e => setEditForm(f => ({ ...f, thumbnail_url: e.target.value }))}
                    />
                  )}
                </div>
                <div>
                  <label className={lbl}>Duration</label>
                  <input className={inp} placeholder="e.g. 8 min" value={editForm.duration_text || ''} onChange={e => setEditForm(f => ({ ...f, duration_text: e.target.value }))} />
                </div>
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={!!editForm.is_published} onChange={e => setEditForm(f => ({ ...f, is_published: e.target.checked }))} className="accent-gold w-4 h-4" />
                <span className="text-cream-300 text-sm">Published (visible to enrolled users)</span>
              </label>
            </div>
            <div className="flex gap-3 px-6 pb-5">
              <button onClick={saveDay} disabled={saving} className="flex items-center gap-2 px-5 py-2.5 bg-gold text-charcoal text-sm font-medium rounded-lg hover:bg-gold/90 disabled:opacity-60 transition-all">
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                Save Day
              </button>
              <button onClick={() => setEditingDay(null)} className="px-5 py-2.5 border border-white/10 text-cream-400 text-sm rounded-lg hover:text-cream transition-colors">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Enrollment override modal ── */}
      {editingEnrollment && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-charcoal border border-white/10 rounded-2xl w-full max-w-sm">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
              <h3 className="text-cream font-medium">Override Enrollment</h3>
              <button onClick={() => setEditingEnrollment(null)} className="text-cream-400 hover:text-cream"><X size={18} /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <p className="text-cream-400 text-xs">{editingEnrollment.user_name} · {editingEnrollment.user_email}</p>
              <div>
                <label className={lbl}>Override Current Day (1–{totalDays}, blank to use date-based)</label>
                <input type="number" min="1" max={totalDays} className={inp} value={enrollForm.current_day_override} onChange={e => setEnrollForm(f => ({ ...f, current_day_override: e.target.value }))} />
              </div>
              <div>
                <label className={lbl}>Status</label>
                <select className={inp} value={enrollForm.status} onChange={e => setEnrollForm(f => ({ ...f, status: e.target.value }))}>
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={!!enrollForm.is_completed} onChange={e => setEnrollForm(f => ({ ...f, is_completed: e.target.checked }))} className="accent-gold w-4 h-4" />
                <span className="text-cream-300 text-sm">Mark as Completed</span>
              </label>
              <div>
                <label className={lbl}>Admin Notes</label>
                <textarea className={`${inp} h-16 resize-none`} value={enrollForm.admin_notes} onChange={e => setEnrollForm(f => ({ ...f, admin_notes: e.target.value }))} />
              </div>
            </div>
            <div className="flex flex-wrap gap-3 px-6 pb-5">
              <button onClick={saveEnrollment} className="flex items-center gap-2 px-5 py-2.5 bg-gold text-charcoal text-sm font-medium rounded-lg hover:bg-gold/90 transition-all">
                <Save size={13} />
                Save
              </button>
              <button onClick={() => setEditingEnrollment(null)} className="px-5 py-2.5 border border-white/10 text-cream-400 text-sm rounded-lg hover:text-cream transition-colors">
                Cancel
              </button>
              <button
                onClick={() => resetEnrollment(editingEnrollment.id)}
                className="flex items-center gap-2 px-5 py-2.5 border border-red-500/30 text-red-400 text-sm rounded-lg hover:bg-red-500/10 transition-all ml-auto"
              >
                <RotateCcw size={13} />
                Reset to Day 1
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Root AdminChallengeManager ───────────────────────────────────────────────
const AdminChallengeManager = ({ getAuthHeader }) => {
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChallenge, setSelectedChallenge] = useState(null);

  const fetchChallenges = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${backendUrl}/api/admin/challenges`, { headers: getAuthHeader() });
      if (res.ok) setChallenges(await res.json());
    } catch (err) {
      console.error('Challenges fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchChallenges(); }, [fetchChallenges]);

  const handleCreate = async (data) => {
    const res = await fetch(`${backendUrl}/api/admin/challenges`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (res.ok) {
      const c = await res.json();
      setChallenges(prev => [c, ...prev]);
      toast.success('Challenge created');
    } else {
      const err = await res.json();
      throw new Error(err.detail || 'Failed to create');
    }
  };

  const handleDelete = async (challenge) => {
    if (!window.confirm(`Are you sure you want to delete this challenge? This action cannot be undone.`)) return;
    try {
      const res = await fetch(`${backendUrl}/api/admin/challenges/${challenge.id}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      if (res.ok) {
        setChallenges(prev => prev.filter(c => c.id !== challenge.id));
        toast.success('Challenge deleted');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to delete challenge');
      }
    } catch {
      toast.error('Network error');
    }
  };

  if (selectedChallenge) {
    return (
      <ChallengeDetail
        challenge={selectedChallenge}
        getAuthHeader={getAuthHeader}
        onBack={() => { setSelectedChallenge(null); fetchChallenges(); }}
      />
    );
  }

  return (
    <ChallengeList
      challenges={challenges}
      onSelect={setSelectedChallenge}
      onCreate={handleCreate}
      onDelete={handleDelete}
      loading={loading}
    />
  );
};

export default AdminChallengeManager;
